import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '../../lib/trpc';
import { useConfirm } from '../common/ConfirmProvider';
import { EmptyState } from '../common/EmptyState';
import { TableSkeleton } from '../common/Skeleton';
import { RulePreviewDialog } from '../transactions/RulePreviewDialog';

const MATCH_LABELS: Record<string, string> = {
  contains: 'innehåller',
  exact: 'exakt',
  // biome-ignore lint/style/useNamingConvention: matches DB enum value
  starts_with: 'börjar med',
};

export function CategoryRuleList() {
  const { confirm } = useConfirm();
  const query = trpc.categoryRule.list.useQuery();
  const duplicatesQuery = trpc.categoryRule.findDuplicateGroups.useQuery(undefined, {
    enabled: false,
  });
  const categoriesQuery = trpc.category.listGroups.useQuery();
  const utils = trpc.useUtils();

  const deleteMutation = trpc.categoryRule.delete.useMutation({
    onMutate: async ({ id }) => {
      await utils.categoryRule.list.cancel();
      const previous = utils.categoryRule.list.getData();
      utils.categoryRule.list.setData(undefined, (old) => {
        if (!old) return old;
        return old.filter((rule) => rule.id !== id);
      });
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        utils.categoryRule.list.setData(undefined, context.previous);
      }
      toast.error(err.message);
    },
    onSettled: () => {
      void utils.categoryRule.list.invalidate();
      void utils.categoryRule.findDuplicateGroups.invalidate();
    },
    onSuccess: () => {
      toast.success('Regel borttagen');
    },
  });

  const removeDuplicatesMutation = trpc.categoryRule.removeDuplicates.useMutation({
    onSuccess: () => {
      void utils.categoryRule.list.invalidate();
      void duplicatesQuery.refetch();
      toast.success('Duplicater borttagna');
    },
    onError: (err) => toast.error(err.message),
  });

  const removeAllDuplicatesMutation = trpc.categoryRule.removeAllDuplicates.useMutation({
    onSuccess: () => {
      void utils.categoryRule.list.invalidate();
      void duplicatesQuery.refetch();
      toast.success('Alla duplicater borttagna');
    },
    onError: (err) => toast.error(err.message),
  });

  const [adding, setAdding] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [editingRule, setEditingRule] = useState<{
    id: string;
    pattern: string;
    matchType: 'contains' | 'exact' | 'starts_with';
    categoryId: string;
    confidence: string;
  } | null>(null);
  const [preview, setPreview] = useState<{
    pattern: string;
    matchType: 'contains' | 'exact' | 'starts_with';
    categoryId: string;
    categoryName: string;
    confidence: number;
    existingRuleId?: string;
    mode: 'create' | 'edit';
  } | null>(null);
  const [form, setForm] = useState({
    pattern: '',
    matchType: 'contains' as 'contains' | 'exact' | 'starts_with',
    categoryId: '',
    confidence: '100',
  });

  const set = (key: string, value: string) => setForm((current) => ({ ...current, [key]: value }));

  const allCategories = (categoriesQuery.data ?? []).flatMap(
    (group: { name: string; categories: { id: string; name: string }[] }) =>
      group.categories.map((category) => ({
        id: category.id,
        label: `${group.name} / ${category.name}`,
      })),
  );

  const getCategoryLabel = (id: string) =>
    allCategories.find((category) => category.id === id)?.label ?? id;

  const handleAdd = (event: FormEvent) => {
    event.preventDefault();
    const catName = getCategoryLabel(form.categoryId);
    setPreview({
      pattern: form.pattern.trim(),
      matchType: form.matchType,
      categoryId: form.categoryId,
      categoryName: catName,
      confidence: Number.parseInt(form.confidence, 10),
      mode: 'create',
    });
    setAdding(false);
  };

  const handleEdit = (event: FormEvent) => {
    event.preventDefault();
    if (!editingRule) return;
    const catName = getCategoryLabel(editingRule.categoryId);
    setPreview({
      pattern: editingRule.pattern.trim(),
      matchType: editingRule.matchType,
      categoryId: editingRule.categoryId,
      categoryName: catName,
      confidence: Number.parseInt(editingRule.confidence, 10),
      existingRuleId: editingRule.id,
      mode: 'edit',
    });
    setEditingRule(null);
  };

  if (query.isLoading) return <TableSkeleton rows={5} columns={4} />;
  if (query.error) return <p className="text-sm text-destructive">{query.error.message}</p>;

  const rules: {
    id: string;
    pattern: string;
    matchType: string;
    categoryId: string;
    confidence: number;
    linkedBillId?: string | null;
    linkedBillName?: string | null;
  }[] = (query.data ?? []) as {
    id: string;
    pattern: string;
    matchType: string;
    categoryId: string;
    confidence: number;
    linkedBillId?: string | null;
    linkedBillName?: string | null;
  }[];

  const renderRuleActions = (rule: (typeof rules)[number]) => (
    <div className="flex shrink-0 items-center justify-end gap-1">
      <Button
        type="button"
        onClick={() => {
          setEditingRule({
            id: rule.id,
            pattern: rule.pattern,
            matchType: rule.matchType as 'contains' | 'exact' | 'starts_with',
            categoryId: rule.categoryId,
            confidence: String(rule.confidence),
          });
        }}
        variant="ghost"
        size="sm"
        aria-label={`Redigera regeln ${rule.pattern}`}
        className="h-11 px-3 text-xs text-muted-foreground hover:text-foreground sm:h-auto sm:px-2"
      >
        Redigera
      </Button>
      <Button
        type="button"
        onClick={async () => {
          const warning = rule.linkedBillName
            ? `Denna regel är kopplad till räkningen "${rule.linkedBillName}". Räkningen kommer sluta matcha nya transaktioner.\n\n`
            : '';

          const ok = await confirm({
            title: 'Ta bort regel',
            message: `${warning}Är du säker på att du vill ta bort regeln "${rule.pattern}"?`,
            confirmLabel: 'Ta bort',
            destructive: true,
          });
          if (ok)
            deleteMutation.mutate({
              id: rule.id,
            });
        }}
        variant="ghost"
        size="sm"
        aria-label={`Ta bort regeln ${rule.pattern}`}
        className="h-11 px-3 text-xs text-destructive hover:text-destructive/80 sm:h-auto sm:px-2"
      >
        Ta bort
      </Button>
    </div>
  );

  const duplicateData = duplicatesQuery.data;
  const duplicateGroups = duplicateData?.groups ?? [];
  const hasDuplicates = (duplicateData?.totalRedundant ?? 0) > 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Kategoriregler matchar automatiskt transaktionsbeskrivningar mot kategorier vid import.
        </p>
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:ml-4">
          <Button
            type="button"
            onClick={() => {
              const next = !showDuplicates;
              setShowDuplicates(next);
              if (next) {
                void duplicatesQuery.refetch();
              }
            }}
            variant="outline"
          >
            {showDuplicates ? 'Visa alla regler' : 'Visa duplicater'}
          </Button>
          <Button type="button" onClick={() => setAdding(true)}>
            + Lägg till regel
          </Button>
        </div>
      </div>

      {showDuplicates && (
        <Card className="rounded-2xl">
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div>
              <CardTitle className="text-[15px] font-semibold">Potentiella duplicater</CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">
                Regler med samma kategori och matchningstyp grupperas när ett kortare mönster redan
                täcker de längre.
              </p>
            </div>
            {hasDuplicates && (
              <Button
                type="button"
                disabled={removeAllDuplicatesMutation.isPending}
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Ta bort alla duplicater',
                    message: `Är du säker på att du vill ta bort alla ${duplicateData?.totalRedundant ?? 0} duplicerade regler?`,
                    confirmLabel: 'Ta bort',
                    destructive: true,
                  });
                  if (ok) removeAllDuplicatesMutation.mutate();
                }}
                variant="destructive"
              >
                {removeAllDuplicatesMutation.isPending ? 'Tar bort…' : 'Ta bort alla duplicater'}
              </Button>
            )}
          </CardHeader>

          <CardContent>
            <div className="text-xs text-muted-foreground">
              {duplicateData?.totalGroups ?? 0} grupp
              {duplicateData?.totalGroups === 1 ? '' : 'er'} · {duplicateData?.totalRedundant ?? 0}{' '}
              redundant regel
              {(duplicateData?.totalRedundant ?? 0) === 1 ? '' : 'er'}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="overflow-hidden rounded-2xl py-0">
        {showDuplicates ? (
          duplicatesQuery.isLoading ? (
            <CardContent className="p-4 text-sm text-muted-foreground/70">
              Analyserar regler…
            </CardContent>
          ) : duplicateGroups.length === 0 ? (
            <CardContent className="p-4 text-sm text-muted-foreground/70">
              Inga duplicerade regler hittades.
            </CardContent>
          ) : (
            <div className="divide-y divide-border">
              {duplicateGroups.map((group) => {
                const higherConfidenceDuplicate = group.redundantRules.some(
                  (rule) => rule.confidence > group.primaryRule.confidence,
                );

                return (
                  <details key={group.primaryRule.id} className="group px-4 py-3">
                    <summary className="flex list-none cursor-pointer flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                      <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            variant="outline"
                            className="border-positive/25 bg-positive-soft text-positive"
                          >
                            Primär
                          </Badge>
                          <span className="break-all font-mono text-sm text-foreground">
                            {group.primaryRule.pattern}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {MATCH_LABELS[group.primaryRule.matchType] ??
                              group.primaryRule.matchType}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {getCategoryLabel(group.primaryRule.categoryId)} ·{' '}
                          {group.redundantRules.length} redundant regel
                          {group.redundantRules.length === 1 ? '' : 'er'}
                        </p>
                        {higherConfidenceDuplicate && (
                          <p className="text-xs text-warning">
                            En av duplicaterna har högre säkerhet än primärregeln. Den högsta
                            säkerhetsnivån kommer att behållas.
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        onClick={async (event) => {
                          event.preventDefault();
                          const ok = await confirm({
                            title: 'Ta bort duplicater',
                            message: `Är du säker på att du vill ta bort ${group.redundantRules.length} duplicerade regler för "${group.primaryRule.pattern}"?`,
                            confirmLabel: 'Ta bort',
                            destructive: true,
                          });
                          if (ok) {
                            removeDuplicatesMutation.mutate({
                              primaryRuleId: group.primaryRule.id,
                              redundantRuleIds: group.redundantRules.map((rule) => rule.id),
                            });
                          }
                        }}
                        disabled={removeDuplicatesMutation.isPending}
                        variant="ghost"
                        size="sm"
                        className="h-11 self-start px-3 text-xs text-destructive hover:text-destructive/80 sm:h-auto sm:px-2"
                      >
                        Ta bort duplicater
                      </Button>
                    </summary>

                    <div className="mt-3 overflow-x-auto rounded-lg border border-border">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border bg-muted text-xs text-muted-foreground">
                            <th className="py-1.5 pl-3 text-left font-medium">Mönster</th>
                            <th className="px-2 py-1.5 text-left font-medium">Typ</th>
                            <th className="px-2 py-1.5 text-right font-medium">Säkerhet</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.redundantRules.map((rule) => (
                            <tr key={rule.id} className="border-b border-border last:border-0">
                              <td className="break-words py-2 pl-3 pr-2 font-mono text-sm text-foreground">
                                {rule.pattern}
                              </td>
                              <td className="px-2 py-2 text-sm text-muted-foreground">
                                {MATCH_LABELS[rule.matchType] ?? rule.matchType}
                              </td>
                              <td className="px-2 py-2 text-right text-sm text-muted-foreground">
                                {rule.confidence}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                );
              })}
            </div>
          )
        ) : rules.length === 0 ? (
          <EmptyState
            icon="📋"
            title="Inga kategoriregler"
            description="Skapa regler för att automatiskt kategorisera transaktioner vid import."
            action={{
              label: '+ Lägg till regel',
              onClick: () => setAdding(true),
            }}
          />
        ) : (
          <>
            {/* Mobile: stacked rows */}
            <ul className="divide-y divide-border md:hidden">
              {rules.map((rule) => (
                <li key={rule.id} className="pt-3 pb-1 pl-4 pr-2">
                  <div className="break-all pr-2 font-mono text-sm text-foreground">
                    {rule.pattern}
                  </div>
                  <div className="mt-0.5 pr-2 text-xs text-muted-foreground">
                    {getCategoryLabel(rule.categoryId)}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0 truncate text-xs text-muted-foreground/70">
                      {MATCH_LABELS[rule.matchType] ?? rule.matchType} · Säkerhet {rule.confidence}
                    </span>
                    {renderRuleActions(rule)}
                  </div>
                </li>
              ))}
            </ul>

            {/* Desktop: table */}
            <table className="hidden w-full md:table">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground">
                  <th className="py-1.5 pl-4 text-left font-medium">Mönster</th>
                  <th className="px-2 py-1.5 text-left font-medium">Typ</th>
                  <th className="px-2 py-1.5 text-left font-medium">Kategori</th>
                  <th className="px-2 py-1.5 text-right font-medium">Säkerhet</th>
                  <th className="py-1.5 pr-4 text-right font-medium">
                    <span className="sr-only">Åtgärder</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr
                    key={rule.id}
                    className="border-b border-border last:border-0 hover:bg-muted/50"
                  >
                    <td className="break-words py-2 pl-4 pr-2 font-mono text-sm text-foreground">
                      {rule.pattern}
                    </td>
                    <td className="px-2 py-2 text-sm text-muted-foreground">
                      {MATCH_LABELS[rule.matchType] ?? rule.matchType}
                    </td>
                    <td className="px-2 py-2 text-sm text-foreground">
                      {getCategoryLabel(rule.categoryId)}
                    </td>
                    <td className="px-2 py-2 text-right text-sm text-muted-foreground">
                      {rule.confidence}
                    </td>
                    <td className="py-2 pr-4 text-right">{renderRuleActions(rule)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Card>

      {adding && (
        <Dialog open onOpenChange={(open) => !open && setAdding(false)}>
          <DialogContent showCloseButton={false}>
            <DialogHeader>
              <DialogTitle>Lägg till kategoriregel</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-3">
              <div>
                <Label htmlFor="rule-pattern" className="text-xs text-muted-foreground">
                  Mönster
                </Label>
                <Input
                  id="rule-pattern"
                  required
                  value={form.pattern}
                  onChange={(event) => set('pattern', event.target.value)}
                  placeholder="t.ex. ICA, Spotify"
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label htmlFor="rule-match-type" className="text-xs text-muted-foreground">
                  Matchningstyp
                </Label>
                <Select value={form.matchType} onValueChange={(value) => set('matchType', value)}>
                  <SelectTrigger id="rule-match-type" className="mt-1 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contains">Innehåller</SelectItem>
                    <SelectItem value="starts_with">Börjar med</SelectItem>
                    <SelectItem value="exact">Exakt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="rule-category" className="text-xs text-muted-foreground">
                  Kategori
                </Label>
                <Select value={form.categoryId} onValueChange={(value) => set('categoryId', value)}>
                  <SelectTrigger id="rule-category" className="mt-1 w-full" aria-required="true">
                    <SelectValue placeholder="Välj kategori…" />
                  </SelectTrigger>
                  <SelectContent>
                    {allCategories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="rule-confidence" className="text-xs text-muted-foreground">
                  Säkerhet (1–100)
                </Label>
                <Input
                  id="rule-confidence"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100}
                  value={form.confidence}
                  onChange={(event) => set('confidence', event.target.value)}
                  className="mt-1"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAdding(false)}>
                  Avbryt
                </Button>
                <Button type="submit">Förhandsgranska</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {editingRule && (
        <Dialog open onOpenChange={(open) => !open && setEditingRule(null)}>
          <DialogContent showCloseButton={false}>
            <DialogHeader>
              <DialogTitle>Redigera kategoriregel</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEdit} className="space-y-3">
              <div>
                <Label htmlFor="edit-rule-pattern" className="text-xs text-muted-foreground">
                  Mönster
                </Label>
                <Input
                  id="edit-rule-pattern"
                  required
                  value={editingRule.pattern}
                  onChange={(e) =>
                    setEditingRule((prev) =>
                      prev
                        ? {
                            ...prev,
                            pattern: e.target.value,
                          }
                        : prev,
                    )
                  }
                  className="mt-1 font-mono"
                />
              </div>
              <div>
                <Label htmlFor="edit-rule-match-type" className="text-xs text-muted-foreground">
                  Matchningstyp
                </Label>
                <Select
                  value={editingRule.matchType}
                  onValueChange={(value) =>
                    setEditingRule((prev) =>
                      prev
                        ? {
                            ...prev,
                            matchType: value as 'contains' | 'exact' | 'starts_with',
                          }
                        : prev,
                    )
                  }
                >
                  <SelectTrigger id="edit-rule-match-type" className="mt-1 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contains">Innehåller</SelectItem>
                    <SelectItem value="starts_with">Börjar med</SelectItem>
                    <SelectItem value="exact">Exakt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-rule-category" className="text-xs text-muted-foreground">
                  Kategori
                </Label>
                <Select
                  value={editingRule.categoryId}
                  onValueChange={(value) =>
                    setEditingRule((prev) => (prev ? { ...prev, categoryId: value } : prev))
                  }
                >
                  <SelectTrigger
                    id="edit-rule-category"
                    className="mt-1 w-full"
                    aria-required="true"
                  >
                    <SelectValue placeholder="Välj kategori…" />
                  </SelectTrigger>
                  <SelectContent>
                    {allCategories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-rule-confidence" className="text-xs text-muted-foreground">
                  Säkerhet (1–100)
                </Label>
                <Input
                  id="edit-rule-confidence"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100}
                  value={editingRule.confidence}
                  onChange={(e) =>
                    setEditingRule((prev) =>
                      prev
                        ? {
                            ...prev,
                            confidence: e.target.value,
                          }
                        : prev,
                    )
                  }
                  className="mt-1"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditingRule(null)}>
                  Avbryt
                </Button>
                <Button type="submit">Förhandsgranska</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {preview && (
        <RulePreviewDialog
          open
          onClose={() => setPreview(null)}
          pattern={preview.pattern}
          matchType={preview.matchType}
          categoryId={preview.categoryId}
          categoryName={preview.categoryName}
          existingRuleId={preview.existingRuleId}
          mode={preview.mode}
        />
      )}
    </div>
  );
}
